/**
 * The map registry.
 *
 * Everything that loads a level goes through `DEFAULT_MAP` / `MAPS` rather
 * than importing a specific map module, so that adding, renaming or repointing
 * a level is a change to this file and nowhere else.
 *
 * Every map module here is generated — they are compiled from the ASCII
 * plans in packages/maptool/maps/ by `npm run maps -w @prop-hunt/maptool`,
 * which is also what refuses to emit a level with a solid too thin to collide
 * with, a spawn inside geometry, a room nothing can walk to, or a hunter spawn
 * that can see a hider spawn. The ASCII plan is the artefact the level lives
 * in; the module is a build product that happens to be checked in, because
 * shared/ has no build step and no dependencies and nothing at install time
 * can be relied on to run the converter.
 *
 * Maps are keyed by a stable string id, not by array index: the id is what a
 * lobby would carry to say which level it is on, and reordering this file must
 * never silently move a session onto different geometry. `mapHash` is the
 * check that catches it if it does — see the join handshake.
 */

import type { GameMap } from "../mapfmt.js";
import { DEPOT_MAP } from "./depot.js";
import { OFFICE_MAP } from "./office.js";
import { ROOFTOPS_MAP } from "./rooftops.js";

/**
 * The level a lobby lands on when it did not ask for one.
 *
 * depot rather than office because the e2e suite's hunter routes are waypoint
 * lists generated against depot's geometry (packages/e2e/scripts/find-route.ts),
 * so repointing this would invalidate them silently — a test that still passes
 * while walking a route that no longer exists is worse than no test.
 */
export const DEFAULT_MAP_ID = "depot";

export const DEFAULT_MAP: GameMap = DEPOT_MAP;

/**
 * Insertion order is the order a level picker offers them in, so the default
 * comes first. Nothing else may depend on the order — see the note above about
 * keying by id.
 */
export const MAPS: ReadonlyMap<string, GameMap> = new Map([
  [DEFAULT_MAP_ID, DEPOT_MAP],
  ["office", OFFICE_MAP],
  ["rooftops", ROOFTOPS_MAP],
]);
